import type { RouteTo } from '@/app/movedPage';
import { movedPages } from '@/app/movedPages';
import type { RouterAuth } from '@/app/routerAuth';
import { canUseNewNavigation } from '@/shared/permissions/pagePermissions';
import type { Session } from '@/shared/permissions/session';

/** A match's `fullPath` as a link's `to` spells its route: without the `/` that ends an index route's. */
export function routeToOf(fullPath: string): string {
    return fullPath.replace(/(.)\/$/, '$1');
}

/**
 * The URL `href` (path, query and fragment, as typed) at the route `to`, where the routes `from` and `to` end in a
 * splat (`/apps/$`, `/next/apps/$`): only the part before the splat changes. `undefined` for other routes. The router
 * decodes a splat and encodes it again, which turns `$` into `%24` and `%2F` into `/`, so a move by params would change
 * an app's path.
 */
export function splatHrefAt(from: string, to: string, href: string): string | undefined {
    if (!from.endsWith('/$') || !to.endsWith('/$')) {
        return undefined;
    }
    const prefix = from.slice(0, -2);
    const rest = href.slice(prefix.length);
    return href.startsWith(prefix) && /^(?:[/?#]|$)/.test(rest) ? to.slice(0, -2) + rest : undefined;
}

/**
 * The new route of the page at `to`, or `undefined` when the map doesn't list it. It takes the page's params as they
 * are, which `movedPage` has checked.
 */
export function newRouteOf(to: string): RouteTo | undefined {
    return movedPages.find(({ from }) => from === to)?.to;
}

/**
 * The old route of the page whose new route is `to`, where `session` is an Internal user who may not use the new
 * navigation: one who opened a colleague's link, or kept a tab open when the flag was turned off, gets the page in
 * today's navigation. `undefined` when the map doesn't list `to` as a new route, and for customers and supplier users,
 * who never had the new navigation and stay refused there.
 */
export function oldRouteFor(session: Session, to: string): RouteTo | undefined {
    if (session.userType !== 'Internal' || canUseNewNavigation(session)) {
        return undefined;
    }
    return movedPages.find((page) => page.to === to)?.from;
}

/** Whether the new navigation is on for a navigation: available to the user, and chosen in this browser. */
export function isNewNavigationOn(session: Session): boolean {
    return canUseNewNavigation(session) && isNewNavigationChosen();
}

/**
 * For a page view, from the page's matches: `not_enabled` where the user may not use the new navigation, else whether
 * the page is in the new one or in today's. The route's name alone can't tell a user who has the new navigation but
 * stays in today's from one who doesn't have it. `undefined` signed out, without a session.
 */
export function newNavigationOfPage(
    // `auth`, which every route's context has: a type of optional properties only would refuse the root's.
    matches: ReadonlyArray<{ routeId: string; context: { auth: RouterAuth; session?: Session } }>,
): 'not_enabled' | 'old' | 'new' | undefined {
    const session = matches.at(-1)?.context.session;
    if (session === undefined) {
        return undefined;
    }
    if (!canUseNewNavigation(session)) {
        return 'not_enabled';
    }
    return matches.some(({ routeId }) => routeId === '/_authenticated/next') ? 'new' : 'old';
}

/** Per browser and per tenant host, since each tenant has its own origin. */
const CHOSEN_KEY = 'new-navigation';

/** Whether this browser chose the new navigation. Never throws: the browser may block storage. */
export function isNewNavigationChosen(): boolean {
    try {
        return localStorage.getItem(CHOSEN_KEY) === 'on';
    } catch {
        return false;
    }
}

/**
 * Saves this browser's choice. Switching back is kept as 'off', not removed: a later default can tell it from never
 * having chosen. Where the browser blocks storage, it stays on today's navigation.
 */
export function chooseNewNavigation(chosen: boolean): void {
    try {
        localStorage.setItem(CHOSEN_KEY, chosen ? 'on' : 'off');
    } catch {
        // Nothing to keep the choice in.
    }
}
